// Company identifiers are case-insensitive tenant keys. Always persist and
// send them in lowercase so "Veolia" and "veolia" resolve to the same tenant.

export const normalizeCompanyIdentifier = (identifier) => {
  if (identifier == null) return "";
  return String(identifier).trim().toLowerCase();
};

export const getStoredCompanyIdentifier = () =>
  normalizeCompanyIdentifier(localStorage.getItem("companyIdentifier"));

export const setStoredCompanyIdentifier = (identifier) => {
  const normalized = normalizeCompanyIdentifier(identifier);
  if (normalized) {
    localStorage.setItem("companyIdentifier", normalized);
  } else {
    localStorage.removeItem("companyIdentifier");
  }
  return normalized;
};

export const getTenantLoginPath = (identifier) => {
  const normalized = normalizeCompanyIdentifier(identifier);
  return normalized ? `/${normalized}/login` : "/login";
};

/**
 * Drop auth/session storage while keeping the tenant identifier so post-logout
 * navigation can still go to /{companyIdentifier}/login.
 */
export const clearAuthStorage = ({ preserveIdentifier = true } = {}) => {
  const companyIdentifier = getStoredCompanyIdentifier();
  const redirectUrl = localStorage.getItem("redirectUrl");

  localStorage.clear();
  sessionStorage.clear();

  if (preserveIdentifier && companyIdentifier) {
    setStoredCompanyIdentifier(companyIdentifier);
  }
  if (redirectUrl) {
    localStorage.setItem("redirectUrl", redirectUrl);
  }

  return preserveIdentifier ? companyIdentifier : "";
};

// Path segments that sit at the root of the router and can never be a tenant.
const NON_TENANT_PATH_SEGMENTS = ["app", "login", "custom-login", "public", "forgot-password"];

/**
 * Read the tenant out of a pathname like /{companyIdentifier}/app/dashboard.
 * Returns "" for tenant-less paths such as /login, which have no second slash.
 */
export const getTenantFromPathname = (pathname) => {
  const segment = normalizeCompanyIdentifier(String(pathname || "").match(/^\/([^/]+)\//)?.[1]);
  return segment && !NON_TENANT_PATH_SEGMENTS.includes(segment) ? segment : "";
};

// Only in-app destinations fall back to the stored identifier. The auth routes
// (/login, /forgot-password, /create-password, /custom-login) are reachable
// without a tenant, and pinning them to a stored identifier would stop the
// identifier step from switching to another site.
const isAppPath = (path) => /^\/?app(\/|$)/.test(String(path || ""));

/**
 * The tenant to use when building URLs: context first, then the current URL,
 * then — for in-app destinations only — the identifier persisted at login.
 * That last fallback is what keeps /app/dashboard prefixed on a tenant-less
 * path such as /login, where the URL carries no tenant and a fresh tab has no
 * session-scoped tenant context.
 */
export const resolveActiveTenant = ({ tenantName, hasTenant, pathname, targetPath } = {}) => {
  const fromContext = hasTenant ? normalizeCompanyIdentifier(tenantName) : "";
  const fromUrl = getTenantFromPathname(pathname);
  return fromContext || fromUrl || (isAppPath(targetPath) ? getStoredCompanyIdentifier() : "");
};
